/**
 * The part of a tracker board that is the same whatever its rows are: which
 * columns it shows, how wide they are, what order they sit in, which custom
 * groups exist, which views are enabled, and getting all of that saved.
 *
 * The layout blob lives on the tracker row and is shared by the whole
 * workspace, so it is seeded once from the tracker prop and every later edit
 * is debounced into one full-replace PATCH. Boards differ in what else they
 * keep in that blob (the briefs board also stores its per-project drag order),
 * which is what `extraPayload` and `onSeed` are for.
 */
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  useUpdateTrackerMutation,
  type Tracker,
  type TrackerDashboard,
  type TrackerLayout,
  type TrackerViewType,
} from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useDebounce } from '@/shared/hooks';

import {
  COLUMN_TYPE_LABEL_KEY,
  COMMENTS_COLUMN_WIDTH,
  CUSTOM_COLUMN_DEFAULT_WIDTH,
  CUSTOM_COLUMN_MIN_WIDTH,
  CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE,
  DEFAULT_COLUMN_ORDER,
  ITEM_COLUMN_DEFAULT_WIDTH,
  ITEM_COLUMN_MIN_WIDTH,
  newColumnId,
  REORDERABLE_COLUMNS,
  defaultStatusOptions,
  storedColumnType,
  uniqueLabel,
  type CustomColumn,
  type CustomColumnType,
  type StatusOption,
} from './board-columns';
import { configToFields, fieldsToConfig } from './column-config';
import { renameFormulaReferences } from './evaluateFormula';
import { useCanEditTracker } from './tracker-access';
import { isReaderLabel, toReaderColumns, withStoredLabels } from './shipped-labels';

/** Debounce window before pushing the user's layout edits to the server. */
const PERSIST_DEBOUNCE_MS = 500;

/** Colors cycled through as the user adds Custom groups. */
const CUSTOM_GROUP_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f97316',
  '#10b981',
  '#06b6d4',
  '#eab308',
  '#ef4444',
];

export interface ColumnDef {
  label: string;
  minWidth: number;
  type?: CustomColumnType;
}

export interface BoardLayoutOptions {
  tracker: Tracker;
  /**
   * Columns for a board whose workspace has never saved a layout. They stay
   * client-side defaults until the first edit persists them, so their ids must
   * be stable: per-row values keyed on them have to survive the move from
   * "seeded default" to "saved layout".
   */
  starterColumns?: () => CustomColumn[];
  /** Last chance to rewrite saved columns on the way in (header migrations). */
  adoptColumns?: (columns: CustomColumn[]) => CustomColumn[];
  /**
   * Columns the board defines itself rather than the user adding them: a
   * record-backed board's columns are the record's own fields. They are legal
   * ids in a saved `columnOrder` and they resolve to a header, so they are
   * needed both to sanitise the order on seed and to build `columnDefs`.
   * Memoise it.
   */
  builtInColumns?: Readonly<Record<string, ColumnDef>>;
  /** Board-specific keys merged into the persisted blob. Memoise it. */
  extraPayload?: Record<string, unknown>;
  /** Read those same board-specific keys back, once, on seed. */
  onSeed?: (layout: TrackerLayout) => void;
}

export function useBoardLayout({
  tracker,
  starterColumns,
  adoptColumns,
  extraPayload,
  onSeed,
  builtInColumns = REORDERABLE_COLUMNS,
}: BoardLayoutOptions) {
  const { t } = useTranslation('trackers');
  const workspaceId = useCurrentWorkspaceId();

  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_COLUMN_ORDER);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [columnLabels, setColumnLabels] = useState<Record<string, string>>({});
  const [itemColumnWidth, setItemColumnWidthState] = useState<number>(ITEM_COLUMN_DEFAULT_WIDTH);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customGroups, setCustomGroups] = useState<StatusOption[]>([]);
  const [dashboard, setDashboard] = useState<TrackerDashboard | null>(null);
  const [views, setViews] = useState<TrackerViewType[]>(() => tracker.layout?.views ?? ['table']);

  // Shared horizontal scroll across groups: a set of scroll containers and a
  // shared scrollLeft value. Direct DOM sync avoids re-render loops.
  const scrollContainersRef = useRef<Set<HTMLDivElement>>(new Set());
  const syncedScrollLeftRef = useRef<number>(0);

  const updateTrackerMutation = useUpdateTrackerMutation({ workspaceId: workspaceId ?? '' });
  const canEdit = useCanEditTracker();
  const hasSeededLayoutRef = useRef(false);
  // The same fact as the ref, in a form a caller can react to. A board that
  // turns its columns into a request has to wait for the saved order, or its
  // first request asks about the pre-seed defaults and is thrown away.
  const [hasSeeded, setHasSeeded] = useState(false);
  const lastSavedSignatureRef = useRef<string | null>(null);
  // Signature currently being upserted. Prevents the debounced effect from
  // firing the same body twice in a row, while still allowing retries on
  // failure (we only mark it saved on success).
  const inFlightSignatureRef = useRef<string | null>(null);

  // Grouping mode — persisted on the tracker row rather than in the layout
  // blob, so the whole workspace shares one grouping.
  const [groupById, setGroupByState] = useState<string | null>(tracker.groupBy ?? null);
  const setGroupBy = useCallback(
    (next: string | null) => {
      setGroupByState(next);
      if (canEdit) {
        updateTrackerMutation.mutate({ trackerId: tracker.id, body: { groupBy: next } });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tracker.id, canEdit],
  );

  // Held in refs so the seed effect stays keyed on the layout alone: a caller
  // that rebuilt one of these inline would otherwise re-seed and throw away
  // everything the user had changed since.
  const seedHooksRef = useRef({ starterColumns, adoptColumns, onSeed, builtInColumns });
  seedHooksRef.current = { starterColumns, adoptColumns, onSeed, builtInColumns };

  useEffect(() => {
    if (hasSeededLayoutRef.current) return;
    const {
      starterColumns: starter,
      adoptColumns: adopt,
      onSeed: readExtras,
      builtInColumns: builtIns,
    } = seedHooksRef.current;
    const data = tracker.layout ?? {};

    // Fresh workspace (no layout ever saved): seed the starter columns so
    // the first visit shows a useful board.
    if (!data.columnOrder && !data.customColumns) {
      const seeded = starter?.() ?? [];
      setCustomColumns(seeded);
      setColumnOrder(seeded.map((c) => c.id));
      setColumnWidths(Object.fromEntries(seeded.map((c) => [c.id, defaultWidth(c.type)])));
      hasSeededLayoutRef.current = true;
      setHasSeeded(true);
      return;
    }

    // Sanitise columnOrder on seed: drop any IDs that no longer correspond
    // to a built-in column or a current custom column. Older layouts may
    // carry stale "type" / "status" entries from when those were fixed
    // columns — leaving them would render nothing but pollute future saves.
    if (data.columnOrder) {
      const customIds = (data.customColumns ?? [])
        .map((c) => c.id)
        .filter((id): id is string => typeof id === 'string');
      const validIds = new Set<string>([...Object.keys(builtIns), ...customIds]);
      setColumnOrder(data.columnOrder.filter((id) => validIds.has(id)));
    }
    if (data.columnWidths) setColumnWidths(data.columnWidths);
    if (data.columnLabels) setColumnLabels(data.columnLabels);
    if (data.itemColumnWidth != null) setItemColumnWidthState(data.itemColumnWidth);
    if (data.customColumns) {
      const restored = data.customColumns.flatMap((c) => {
        // Layout jsonb can hold partially-written columns at runtime despite
        // the non-optional DTO types; keep the guard.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!c.id || !c.label || !c.type) return [];
        const type = c.type as CustomColumnType;
        return [{ id: c.id, label: c.label, type, ...configToFields(type, c.config) }];
      });
      setCustomColumns(adopt ? adopt(restored) : restored);
    }
    if (data.dashboard) setDashboard(data.dashboard);
    if (data.customGroups) setCustomGroups(data.customGroups as StatusOption[]);
    readExtras?.(data);

    hasSeededLayoutRef.current = true;
    setHasSeeded(true);
    // Intentionally DO NOT pre-compute lastSavedSignatureRef from the server
    // data here. The persistence effect below would otherwise fire on the
    // very next tick — when `debouncedPayload` is still the empty defaults
    // from the pre-mount render — and PUT an empty body to the server,
    // wiping the workspace's saved layout. Instead, the persistence effect
    // captures the signature of the *first* post-seed debouncedPayload as
    // its "saved" baseline and skips the network call. After that, real
    // state changes diff against the captured baseline and persist normally.
  }, [tracker.layout]);

  // What the board shows, as opposed to what it saves: a column the platform
  // seeded reads in the language of whoever is looking, while `customColumns`
  // keeps the words the layout actually holds. Everything persisted, and every
  // formula, stays on the stored array — see `shipped-labels.ts`.
  const readerColumns = useMemo(() => toReaderColumns(customColumns, t), [customColumns, t]);

  // Runtime map of column id → display metadata. Built-in columns are merged
  // with user-defined ones so consumers can look up label/minWidth uniformly
  // without caring whether a column is built-in or custom.
  const columnDefs = useMemo<Record<string, ColumnDef>>(() => {
    const defs: Record<string, ColumnDef> = {};
    // Built-in columns get a rename override from columnLabels if present.
    for (const [id, def] of Object.entries(builtInColumns)) {
      defs[id] = { ...def, label: columnLabels[id] ?? def.label };
    }
    // Custom columns carry their label inside customColumns. Per-type
    // min-widths prevent shrinking columns below their natural content width
    // (e.g. an ISO date like "May 13, 2026" or the longest brief-type label).
    for (const col of readerColumns) {
      defs[col.id] = {
        label: col.label,
        minWidth: CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE[col.type],
        type: col.type,
      };
    }
    return defs;
  }, [readerColumns, columnLabels, builtInColumns]);

  const tableMinContentWidth = useMemo(
    () =>
      itemColumnWidth +
      COMMENTS_COLUMN_WIDTH +
      columnOrder.reduce((sum, colId) => {
        // columnWidths/columnDefs are sparse maps keyed by dynamic column ids;
        // eslint reads them as total (noUncheckedIndexedAccess is off) but a
        // stale/absent id genuinely resolves to undefined at runtime.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return sum + (columnWidths[colId] ?? columnDefs[colId]?.minWidth ?? 0);
      }, 0),
    [itemColumnWidth, columnOrder, columnWidths, columnDefs],
  );

  const reorderColumns = useCallback((activeId: string, overId: string) => {
    if (activeId === overId) return;
    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(activeId);
      const newIndex = prev.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const setColumnWidth = useCallback(
    (columnId: string, width: number) => {
      // columnDefs is a sparse map keyed by dynamic column ids (see above).
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const minWidth = columnDefs[columnId]?.minWidth ?? CUSTOM_COLUMN_MIN_WIDTH;
      setColumnWidths((prev) => ({ ...prev, [columnId]: Math.max(width, minWidth) }));
    },
    [columnDefs],
  );

  const setItemColumnWidth = useCallback((width: number) => {
    setItemColumnWidthState(Math.max(width, ITEM_COLUMN_MIN_WIDTH));
  }, []);

  /**
   * Rename a column. Built-in columns write to the columnLabels override map;
   * custom columns mutate their label inside the customColumns array. No-op for
   * unknown ids, blank labels, or labels that match the existing value (avoids
   * spurious persistence writes).
   */
  const renameColumn = useCallback(
    (columnId: string, nextLabel: string) => {
      const trimmed = nextLabel.trim();
      if (trimmed.length === 0) return;

      const builtIn = builtInColumns[columnId] as ColumnDef | undefined;
      if (builtIn) {
        setColumnLabels((prev) => {
          const current = prev[columnId] ?? builtIn.label;
          if (current === trimmed) return prev;
          // Identity-match to the built-in default? Strip the override.
          if (trimmed === builtIn.label) {
            if (!(columnId in prev)) return prev;
            // Rebuild without the key rather than `delete` (no-dynamic-delete).
            return Object.fromEntries(Object.entries(prev).filter(([key]) => key !== columnId));
          }
          return { ...prev, [columnId]: trimmed };
        });
        return;
      }

      setCustomColumns((prev) => {
        const idx = prev.findIndex((c) => c.id === columnId && c.label !== trimmed);
        if (idx === -1) return prev;
        // The rename field was seeded from the header, so a dismissed edit
        // returns the reader's wording rather than the stored one.
        if (isReaderLabel(prev[idx], trimmed, t)) return prev;
        return renameFormulaReferences(prev, prev[idx].label, trimmed).map((column) =>
          column.id === columnId ? { ...column, label: trimmed } : column,
        );
      });
    },
    [builtInColumns, t],
  );

  /**
   * Insert a new custom column after `afterColumnId`, or at the end when that
   * is absent from `columnOrder` (so this is also safe to call from a menu
   * holding a stale id).
   */
  const addCustomColumnAfter = useCallback(
    (afterColumnId: string | null, { type, label }: { type: CustomColumnType; label?: string }) => {
      const typeName = t(COLUMN_TYPE_LABEL_KEY[type]);
      const id = newColumnId();

      setCustomColumns((prev) => [
        ...prev,
        {
          id,
          label:
            label?.trim() ||
            uniqueLabel(
              typeName,
              prev.map((c) => c.label),
            ),
          type,
          ...(type === 'status' ? { statusOptions: defaultStatusOptions() } : {}),
        },
      ]);
      setColumnOrder((order) => {
        const idx = afterColumnId === null ? -1 : order.indexOf(afterColumnId);
        if (idx === -1) return [...order, id];
        return [...order.slice(0, idx + 1), id, ...order.slice(idx + 1)];
      });
      setColumnWidths((widths) => ({ ...widths, [id]: defaultWidth(type) }));
    },
    [t],
  );

  const addCustomColumn = useCallback(
    (spec: { type: CustomColumnType; label?: string }) => addCustomColumnAfter(null, spec),
    [addCustomColumnAfter],
  );

  /**
   * Remove a column from the board. Per-row values for it stay where they are
   * until a future cleanup: they are invisible while the column is gone, and
   * reappear if the same id is ever re-added (unlikely with uuids, but safe).
   */
  const deleteColumn = useCallback((columnId: string) => {
    setColumnOrder((order) => order.filter((id) => id !== columnId));
    setColumnWidths((widths) => withoutKey(widths, columnId));
    setColumnLabels((labels) => withoutKey(labels, columnId));
    setCustomColumns((prev) => {
      if (!prev.some((c) => c.id === columnId)) return prev;
      return prev.filter((c) => c.id !== columnId);
    });
  }, []);

  /**
   * Clone a custom column and insert the copy immediately to its right,
   * carrying type, status options and width. Returns the new column's id so a
   * board that also stores per-row cell values can copy those across; null
   * when there was no such column to clone.
   */
  const duplicateColumn = useCallback(
    (columnId: string): string | null => {
      const source = customColumns.find((c) => c.id === columnId);
      if (!source) return null;

      const newId = newColumnId();
      // Named after the header the user is looking at, not the one underneath
      // it: the copy is theirs from the moment it exists, so "Estado copy" is
      // what they asked for even where the original still stores "Status".
      const sourceLabel = readerColumns.find((c) => c.id === columnId)?.label ?? source.label;
      const label = uniqueLabel(
        t('trackersBoard.useTrackersBoard.columnCopy', { label: sourceLabel }),
        readerColumns.map((c) => c.label),
        (number) =>
          t('trackersBoard.useTrackersBoard.columnCopyNumbered', { label: sourceLabel, number }),
      );

      setCustomColumns((prev) => [
        ...prev,
        {
          id: newId,
          label,
          type: source.type,
          // Deep-copy statusOptions so editing one column's options doesn't
          // mutate the clone (and vice versa).
          ...(source.statusOptions
            ? { statusOptions: source.statusOptions.map((o) => ({ ...o })) }
            : {}),
        },
      ]);
      setColumnOrder((order) => {
        const idx = order.indexOf(source.id);
        if (idx === -1) return [...order, newId];
        return [...order.slice(0, idx + 1), newId, ...order.slice(idx + 1)];
      });
      setColumnWidths((widths) => {
        // widths is a sparse map; a source column with no persisted width
        // resolves to undefined at runtime despite the total-map type.
        const sourceWidth = widths[source.id];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (sourceWidth == null) return widths;
        return { ...widths, [newId]: sourceWidth };
      });

      return newId;
    },
    [customColumns, readerColumns, t],
  );

  const patchColumn = useCallback(
    (columnId: string, type: CustomColumnType, patch: Partial<CustomColumn>) => {
      setCustomColumns((prev) => {
        const idx = prev.findIndex((c) => c.id === columnId && c.type === type);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], ...patch };
        return next;
      });
    },
    [],
  );

  // The editor is seeded from the reader's copy, so an option the user never
  // touched comes back translated. `withStoredLabels` puts those back, so a
  // save that only added one option cannot relabel the board for everyone.
  const updateStatusOptions = useCallback(
    (columnId: string, statusOptions: StatusOption[]) =>
      setCustomColumns((prev) =>
        prev.map((c) =>
          c.id === columnId && c.type === 'status'
            ? { ...c, statusOptions: withStoredLabels(c.statusOptions ?? [], statusOptions, t) }
            : c,
        ),
      ),
    [t],
  );
  const updateButtonCaption = useCallback(
    (columnId: string, buttonCaption: string) => patchColumn(columnId, 'button', { buttonCaption }),
    [patchColumn],
  );
  const updateFormulaExpression = useCallback(
    (columnId: string, formulaExpression: string) =>
      patchColumn(columnId, 'formula', { formulaExpression }),
    [patchColumn],
  );

  const addGroup = useCallback(() => {
    setCustomGroups((prev) => [
      ...prev,
      {
        id: uuidv4(),
        label: uniqueLabel(
          t('trackersBoard.useTrackersBoard.newGroup'),
          prev.map((g) => g.label),
          (number) => t('trackersBoard.useTrackersBoard.newGroupNumbered', { number }),
        ),
        color: CUSTOM_GROUP_COLORS[prev.length % CUSTOM_GROUP_COLORS.length],
      },
    ]);
  }, [t]);
  const renameGroup = useCallback((groupId: string, label: string) => {
    setCustomGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, label } : g)));
  }, []);
  const deleteGroup = useCallback((groupId: string) => {
    setCustomGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  // Build the payload only when one of its inputs actually changes — keeps the
  // useDebounce reference stable across unrelated re-renders.
  const persistedPayload = useMemo(
    () => ({
      columnOrder,
      columnWidths,
      columnLabels,
      itemColumnWidth,
      customColumns: customColumns.map((c) => ({
        id: c.id,
        label: c.label,
        type: storedColumnType(c.type),
        ...fieldsToConfig(c),
      })),
      customGroups,
      // Layout PATCHes are full-replace — the dashboard + views must ride
      // along or a column/width/group edit would silently wipe them.
      ...(dashboard ? { dashboard } : {}),
      views,
      ...extraPayload,
    }),
    [
      columnOrder,
      columnWidths,
      columnLabels,
      itemColumnWidth,
      customColumns,
      customGroups,
      dashboard,
      views,
      extraPayload,
    ],
  );
  const debouncedPayload = useDebounce(persistedPayload, PERSIST_DEBOUNCE_MS);

  useEffect(() => {
    if (!hasSeededLayoutRef.current) return;
    if (!workspaceId) return;
    // A view-only member's sort, grouping and widths stay on their screen.
    if (!canEdit) return;
    const signature = JSON.stringify(debouncedPayload);
    // First post-seed run: accept whatever debouncedPayload currently holds
    // as our "saved" baseline. No PUT. This eats the transient empty-default
    // payload that would otherwise wipe the server (see seed effect comment).
    if (lastSavedSignatureRef.current === null) {
      lastSavedSignatureRef.current = signature;
      return;
    }
    if (signature === lastSavedSignatureRef.current) return;
    if (signature === inFlightSignatureRef.current) return;
    inFlightSignatureRef.current = signature;
    updateTrackerMutation.mutate(
      // Cast: `extraPayload` is an open record, so spreading it gives the
      // literal an index signature the DTO does not have. The column types
      // themselves need no cast — `storedColumnType` narrows them.
      { trackerId: tracker.id, body: { layout: debouncedPayload as TrackerLayout } },
      {
        onSuccess: () => {
          // Only mark this signature as saved once the server confirms.
          lastSavedSignatureRef.current = signature;
          inFlightSignatureRef.current = null;
        },
        onError: () => {
          // Don't update lastSavedSignatureRef — the next state change resends
          // the latest payload.
          inFlightSignatureRef.current = null;
        },
      },
    );
    // We intentionally exclude the mutation from deps — its identity
    // changes on every render and would otherwise re-fire this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPayload, workspaceId, tracker.id, canEdit]);

  return {
    hasSeeded,
    columnOrder,
    columnWidths,
    columnLabels,
    columnDefs,
    // Deliberately the reader's copy, not the stored one. Persistence happens
    // inside this hook against the state, so nothing a caller does with these
    // can write a translated label back to the layout.
    customColumns: readerColumns,
    setCustomColumns,
    itemColumnWidth,
    setItemColumnWidth,
    setColumnWidth,
    reorderColumns,
    tableMinContentWidth,
    addCustomColumn,
    addCustomColumnAfter,
    duplicateColumn,
    deleteColumn,
    renameColumn,
    updateStatusOptions,
    updateButtonCaption,
    updateFormulaExpression,
    customGroups,
    setCustomGroups,
    addGroup,
    renameGroup,
    deleteGroup,
    groupById,
    setGroupBy,
    dashboard,
    setDashboard,
    views,
    setViews,
    scrollContainersRef,
    syncedScrollLeftRef,
  };
}

function defaultWidth(type: CustomColumnType): number {
  return Math.max(CUSTOM_COLUMN_DEFAULT_WIDTH, CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE[type]);
}

/** Rebuild without the key rather than `delete` (no-dynamic-delete). */
function withoutKey<T>(map: Record<string, T>, key: string): Record<string, T> {
  if (!(key in map)) return map;
  return Object.fromEntries(Object.entries(map).filter(([k]) => k !== key));
}

export type ScrollContainersRef = RefObject<Set<HTMLDivElement>>;
export type SyncedScrollLeftRef = RefObject<number>;
export type BoardLayout = ReturnType<typeof useBoardLayout>;
